import React from 'react';

import {useSelector} from 'react-redux';

import {RTKGenericAttachedItemsSidebar} from 'app/components/general/GenericAttachedItemsSidebar/RTKGenericAttachedItemsSidebar';
import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {CurrentUserSelector} from 'app/current-user/api';
import {getCurrentUserPerms} from 'app/current-user/utils';
import {keepUpdatedEnhancer} from 'app/general/keepUpdatedEnhancer';
import {sweetAPI} from 'app/store/sweet-api';
import {runWhenAllRelatedMatch} from 'app/subscriptions';
import {
  convertLocationParamsToFetchParams,
  TEAMTAG,
  useTeams,
} from 'app/teams/api';
import {TEAMS_CREATE_PAGE_URL} from 'app/urls';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';

import type {
  ModelChangeMessageType,
  ModelSubscribedComponentPropsType,
} from 'app/subscriptions';
import type {
  TeamMembershipSerializedType,
  TeamSerializedType,
} from 'sweet-types';

type Props = {
  objectId: number | string;
  objectContentType: 'procedure' | 'proceduregroup' | 'policy' | 'quiz';
  objectURL: string;
  attachedTeams: Array<TeamSerializedType>;
  addToTeam: (
    item: TeamSerializedType,
    urls: Array<{
      url: string;
    }>
  ) => Promise<any>;
  removeFromTeam: (
    item: TeamSerializedType,
    memberships: {
      team_memberships: Array<TeamMembershipSerializedType>;
    }
  ) => Promise<any>;
  canEditObject: boolean;
  objectTeamsMembership: Array<TeamMembershipSerializedType>;
  onClose: () => void;
} & ModelSubscribedComponentPropsType;

export const RTKDetailPageGenericTeamsSidebar = keepUpdatedEnhancer<Props>(
  ({
    objectURL,
    objectId,
    canEditObject,
    attachedTeams,
    addToTeam,
    removeFromTeam,
    objectTeamsMembership,
    objectContentType,
    ...other
  }) => {
    const {isManager, isSuperManager} = getCurrentUserPerms(
      useSelector((state) => CurrentUserSelector(state)?.data)
    );

    const actions = useAsyncActions({
      invalidateTags: sweetAPI.util.invalidateTags,
    });

    const props = {
      itemNameProcessor: (team) => team.name,
      title: gt('Teams'),
      handleModelChange: (message: ModelChangeMessageType) => {
        runWhenAllRelatedMatch(
          message,
          'team',
          {
            content_type: objectContentType,
            id: typeof objectId == 'string' ? parseInt(objectId) : objectId,
          },
          () => {
            // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
            actions.invalidateTags([{type: TEAMTAG, id: 'list'}]);
          }
        );
      },
      onRemove: (team) =>
        removeFromTeam(team, {
          team_memberships: objectTeamsMembership,
        }),
      // attach
      showFilterField: canEditObject,
      searchPlaceholder: gt('Search teams...'),
      onAttach: (team) =>
        addToTeam(team, [
          {
            url: objectURL,
          },
        ]),
      // create
      attachedItems: attachedTeams,
      canCreate: isSuperManager,
      createLabel: gt('Create team'),
      createURL: TEAMS_CREATE_PAGE_URL,
      emptyResultsComponents: (
        <NoResultsMessage
          onSidebar={true}
          icon="fa-users"
          head={gt('No Teams')}
        >
          {isManager && canEditObject
            ? gt(
                `You can add this ${
                  getContentType(objectContentType).name
                } to existing teams, remove it from a team or create a new team using the button below`
              )
            : null}
        </NoResultsMessage>
      ),
      useDataHook: useTeams,
      paramsConverter: convertLocationParamsToFetchParams,
      ...other,
    };

    return (
      <RTKGenericAttachedItemsSidebar //  show/edit items
        {...props}
      />
    );
  }
);
